/**
 * @file Work slip overlay view
 * @author Maarten Lensink
 */
import { AfterViewInit, OnDestroy } from '@angular/core';
import { EntityQuery, EntityState } from 'breeze-client';
import * as moment from 'moment';
import { OverlayProvider } from '../../ui/overlay-provider';
import { AlertsService } from '../../core/alerts.service';
import { DomainService } from '../../domain.service';
import { WorkSlipsService } from '../workslips.service';
import { OverlayService } from '../../core/overlay.service';
import { WorkSlipRelationComponent } from '../workslip-relation/workslip-relation.component';
var WorkSlipViewComponent = /** @class */ (function () {
    /**
     * Creates an instance of WorkSlipViewComponent.
     * @param {OverlayProvider} overlayProvider Contains the assignment based on which the work slip entity will be constructed.
     * @param {OverlayService} overlayService Can close the overlay from the code.
     * @param {WorkSlipsService} workSlipsService The domain service for work slips.
     * @param {DomainService} domainService The planning domain service.
     * @param {AlertsService} alertsService Can create alerts.
     */
    function WorkSlipViewComponent(overlayProvider, overlayService, workSlipsService, domainService, alertsService) {
        var _this = this;
        this.overlayProvider = overlayProvider;
        this.overlayService = overlayService;
        this.workSlipsService = workSlipsService;
        this.domainService = domainService;
        this.alertsService = alertsService;
        this.requiredFields = [];
        /**
         * The spend time entities within the work slip domain.
         * @type {Entity[]}
         */
        this.spendTimes = [];
        this.viewInitialized = false;
        var config = overlayProvider.configuration;
        if (!config.components || config.components.length !== 4) {
            console.warn('WorkSlipView needs configuration for four components.');
            this.overlayService.closeOverlay();
            return;
        }
        this.assignmentConfiguration = config.components[0].configuration;
        this.realisationConfiguration = config.components[1].configuration;
        this.customerConfiguration = config.components[2].configuration;
        this.signatureConfiguration = config.components[3].configuration;
        var assignment = this.overlayProvider.entity;
        if (assignment['Item']) {
            assignment = assignment['Item'];
        }
        var projectName = '';
        var taskName = '';
        if (assignment['Deployment'] && assignment['Deployment']['Demand'] && assignment['Deployment']['Demand']['Task']) {
            var task = assignment['Deployment']['Demand']['Task'];
            taskName = task['Name'];
            if (task['Project']) {
                projectName = task['Project']['Name'];
            }
        }
        var resourceName = '';
        var resourceOid = '';
        if (assignment['Resource']) {
            var fullName = assignment['Resource']['FirstName'] + ' ' + assignment['Resource']['Prefix'] + ' ' + assignment['Resource']['Name'];
            resourceName = fullName.trim();
            resourceOid = assignment['Resource']['Oid'];
        }
        var type = this.workSlipsService.getEntityType('WorkSlip');
        var date = moment();
        date.startOf('day');
        var workSlip = type.createEntity({
            AssignmentOid: assignment['Oid'],
            Project: projectName,
            Task: taskName,
            Name: assignment['Name'],
            Resource: resourceName,
            Resource_Oid: resourceOid,
            Start: assignment['Start'],
            Finish: assignment['Finish'],
            Closed: assignment['Closed'],
            Status: assignment['WorkStatus'],
            SignatureName: '',
            SignatureDate: date
        });
        this.workSlipsService.expandWith(assignment, 'Customer.City', true, function () {
            var customer = assignment['Customer'];
            if (customer) {
                workSlip['CustomerOid'] = customer['Oid'];
                workSlip['CustomerNumber'] = customer['Number'];
                var fullName = customer['FirstName'] + ' ' + customer['Prefix'] + ' ' + customer['Name'];
                workSlip['CustomerName'] = fullName.trim();
                if (customer['City']) {
                    workSlip['CustomerAddress'] = customer['City']['Address'] + ' ' + customer['City']['HouseNumber'];
                    workSlip['CustomerPostalCode'] = customer['City']['ZipCode'];
                    workSlip['CustomerPlace'] = customer['City']['Name'];
                }
                else {
                    workSlip['CustomerAddress'] = '';
                    workSlip['CustomerPostalCode'] = '';
                    workSlip['CustomerPlace'] = '';
                }
                workSlip['CustomerTelephone'] = customer['Tel'];
                workSlip['CustomerEmail'] = customer['EMailAddress'];
                workSlip['CustomerWebsite'] = customer['Url'];
            }
        });
        this.workSlip = this.workSlipsService.addEntity(workSlip);
        type = this.workSlipsService.getEntityType('SpendTime');
        this.workSlipSpendTimeType = type;
        type = this.domainService.getEntityType('SpendTime');
        this.domainSpendTimeType = type;
        this.getSpendTimes();
        this.requiredFields = [];
        this.signatureConfiguration.data.fields.forEach(function (field) {
            if (field.required) {
                _this.requiredFields.push(field.name);
            }
        });
    }
    /**
     * Sets a boolean indicating that the view is initialized, so that the relation view child can be reached.
     */
    WorkSlipViewComponent.prototype.ngAfterViewInit = function () {
        this.viewInitialized = true;
        this.getSpendTimes();
    };
    /**
     * Creates the spend times belonging to the work slip.
     */
    WorkSlipViewComponent.prototype.getSpendTimes = function () {
        var _this = this;
        if (!this.viewInitialized || !this.workSlip || !this.domainSpendTimeType || !this.workSlipSpendTimeType) {
            return;
        }
        var self = this;
        // const query = EntityQuery.from('SpendTimeItems').where('Account_Oid', '==', self.workSlip['AssignmentOid']);
        // Deze is sneller dan die hier boven.
        var query = EntityQuery.from('SpendTime').withParameters({ className: 'Assignment', oid: self.workSlip['AssignmentOid'] });
        this.domainService.entityManager.executeQuery(query).then(function (data) {
            data.results.forEach(function (domainSpendTime) {
                var spendTime = self.workSlipSpendTimeType.createEntity({
                    WorkSlipId: self.workSlip['Id'],
                    WorkSlip: self.workSlip,
                    Start: domainSpendTime['Start'],
                    Finish: domainSpendTime['Finish'],
                    Effort: domainSpendTime['Effort'],
                    TravelTime: domainSpendTime['TravelTime'],
                    Notes: domainSpendTime['Notes']
                });
                self.spendTimes.push(self.workSlipsService.addEntity(spendTime));
            });
            _this.realisationComponent.items = self.spendTimes;
            // inlineCount is a property, but too nested.
            _this.realisationComponent['inlineCount'] = self.spendTimes.length;
        }).catch(function (error) {
            _this.alertsService.createAlert('danger', 'APP.WORKSLIP.FAILED_LOADING_SPENDTIMES');
        });
    };
    /**
     * Returns whether the work slip is signed and ready to be saved.
     * @returns {boolean} A value indicating whether the work slip can be saved.
     */
    WorkSlipViewComponent.prototype.canSave = function () {
        var _this = this;
        var result = true;
        if (!this.workSlip || this.requiredFields.length <= 0) {
            result = false;
        }
        this.requiredFields.forEach(function (field) {
            if (!_this.workSlip[field]) {
                result = false;
            }
        });
        return result;
    };
    /**
     * Saves the new created work slip and its spend times.
     * @param {any} event The click event that triggered the method.
     */
    WorkSlipViewComponent.prototype.saveItem = function (event) {
        var _this = this;
        if (!this.canSave()) {
            this.alertsService.createAlert('warning', 'APP.DOMAIN.VALIDATORS.REQUIRED_FIELD_MISSING');
            return;
        }
        var self = this;
        if (event) {
            event.target.classList.add('sym-working');
        }
        this.workSlipsService.saveChanges(function (errors) {
            // Remove sym-working to show that action is finished.
            if (event) {
                event.target.classList.remove('sym-working');
            }
            // Give correct alert based on whether an error is given or not.
            if (errors) {
                self.alertsService.createAlert('warning', 'APP.WORKSLIP.WORKSLIP_UNSUCCESSFULLY_SIGNED', _this.workSlipsService.entityErrorInfo(errors.entityErrors));
            }
            else {
                self.alertsService.createAlert('success', 'APP.WORKSLIP.WORKSLIP_SUCCESSFULLY_SIGNED');
                self.overlayService.closeOverlay();
            }
        });
    };
    /**
     * Cancels the creation of the work slip entity and its spend times.
     */
    WorkSlipViewComponent.prototype.ngOnDestroy = function () {
        if (this.workSlip && this.workSlip.entityAspect.entityState === EntityState.Added) {
            this.workSlip.entityAspect.rejectChanges();
        }
        this.spendTimes.forEach(function (spendTime) {
            if (spendTime.entityAspect.entityState === EntityState.Added) {
                spendTime.entityAspect.rejectChanges();
            }
        });
    };
    /**
     * Closes the overlay.
     */
    WorkSlipViewComponent.prototype.cancel = function () {
        this.overlayService.closeOverlay();
    };
    return WorkSlipViewComponent;
}());
export { WorkSlipViewComponent };
